import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal, viewChild, DOCUMENT } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';

import { of } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { humanize, IPackage } from '../types';
import { CartSummaryComponent } from '../old/cart-summary/cart-summary.component';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../auth.service';
import { LocationService } from '../location.service';
import { ProfileService } from '../old/profile/profile.service';
import { DateTime } from 'luxon';
import { backend, travel } from '../../environments/environment';
import { MatDividerModule } from '@angular/material/divider';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatError, MatHint } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { CardInputComponent } from '../card-input/card-input.component';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter } from '@angular/material/core';
import { PaymentIntent } from '@stripe/stripe-js';
import { PaintDirective } from '../paint.directive';
import { ConfirmPopup } from '../confirm.popup';
import { Analytics, logEvent } from '@angular/fire/analytics';

@Component({
  selector: 'coupon-checkout',
  templateUrl: './coupon-checkout.component.html',
  styleUrls: ['./coupon-checkout.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideLuxonDateAdapter()],
  imports: [CartSummaryComponent, ReactiveFormsModule, MatDividerModule, MatDatepickerModule, MatHint, MatError, MatIconModule, MatSlideToggleModule, RouterLink, MatButtonModule, CardInputComponent, PaintDirective]
})
export default class CouponCheckoutComponent {
  http = inject(HttpClient);
  profile = inject(ProfileService);
  router = inject(LocationService);
  destroy = inject(DestroyRef);
  toast = inject(ToastrService);
  auth = inject(AuthService);
  activated = inject(ActivatedRoute);
  document = inject(DOCUMENT);
  luxonDateAdapter = inject(DateAdapter);
  confirm = inject(ConfirmPopup);
  ga = inject(Analytics);

  cartSummary = viewChild<CartSummaryComponent>(CartSummaryComponent);
  cardInput = viewChild<CardInputComponent>(CardInputComponent);

  plan = input.required<string>();

  checkoutForm: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    fullname: new FormControl('', [Validators.required]),
    start: new FormControl<DateTime|null>(null, [Validators.required]),
    end: new FormControl<DateTime|null>(null, [Validators.required]),
    terms: new FormControl(false, [Validators.requiredTrue]),
    recipient: new FormControl('', [Validators.required]),
    recipient_email: new FormControl('', [Validators.required, Validators.email]),
    message: new FormControl('', [Validators.required]),
    digital: new FormControl(true)
  });

  get terms(): FormControl {
    return this.checkoutForm.get('terms') as FormControl;
  }

  minDate = DateTime.now().startOf('day').toJSDate();
  maxDate = DateTime.now().plus({ months: 6 }).startOf('day').toJSDate();

  endMinDateTime = signal<DateTime>(DateTime.now().startOf('day'));
  endMaxDateTime = signal<DateTime>(DateTime.now().plus({ days: 1 }).startOf('day'));

  loading = signal<boolean>(false);
  gift_mode = signal<boolean>(false);
  pkg = signal<IPackage|null>(null);

  endMinDate = computed(() => this.endMinDateTime().toJSDate());
  endMaxDate = computed(() => this.endMaxDateTime().toJSDate());

  constructor() { 
    this.luxonDateAdapter.getFirstDayOfWeek = () => 1;

    toObservable(this.plan).pipe(filter(p => !!p), takeUntilDestroyed(this.destroy), switchMap(plan => plan ? this.http.get<IPackage>(backend + 'package/info', { params: { slug: plan, retail: true } }) : of(null))).subscribe(pkg => {
      this.gift_mode.set(pkg?.slug == 'yearly');

      const toDisable = [];
      const toEnable = [];

      if(this.gift_mode()){
        toDisable.push('start', 'end');
        toEnable.push('recipient', 'recipient_email', 'message');
      } else {
        toDisable.push('recipient', 'recipient_email', 'message');
        toEnable.push('start', 'end');
      }

      toEnable.forEach(field => this.checkoutForm.get(field)?.enable());
      toDisable.forEach(field => this.checkoutForm.get(field)?.disable());
      this.pkg.set(pkg);
    });
  }

  onPayment(payment: { intent: PaymentIntent }) {
    const details = this.checkoutForm.value || {};

    this.profile.handleCouponPayment(payment.intent.id, details).pipe(takeUntilDestroyed(this.destroy)).subscribe({
      next: response => {
        if(response && response.code){

          const final_price = Number(response.price);

          const plan = this.gift_mode() ? 'gift' : this.plan();

          logEvent(this.ga, 'purchase', {
            currency: 'EUR',
            value: final_price,
            transaction_id: response.transaction,
            items: [
              {
                item_id: plan,
                item_name: humanize(plan)!,
                price: final_price,
                quantity: 1
              }
            ]
          });

          if(this.gift_mode()){
              const result_message = 'Your recipient has been sent an email with their gift code!' ;

              this.router.navigate(['/premium/gift']).then(r => {
                this.confirm.open('Gift purchased', result_message, 'Gift purchase success prompt', [
                  {
                    label: 'Ok',
                    paint: 'primary',
                    close: true
                  }
                ])
                this.toast.success(result_message, 'Gift purchased successfully!');
              });

          } else {

            if(this.router.isFromIframe()){
              const atag = this.document.createElement('a');
              atag.href = `${travel}/success.html?currency=EUR&transaction_id=${response.transaction}&value=${response.price}&code=${response.code}&package_name=${response.package_name}&package_slug=${response.package_slug}&package_id=${response.package_id}`;
              atag.target = '_top';

              atag.click();
            } else {
              this.router.navigate(['/post-purchase'], { queryParams: { code: response.code, plan: response.package_slug }});
            }
          }

        }
      }
    })
  }

  goNext(): void {

    if(this.checkoutForm.invalid){

      if(this.terms.invalid){
        this.toast.error('Please accept the terms and conditions to proceed.');
      } else {
        this.toast.error('Please make sure all fields were inputted, email is valid, name is provided and date is correct.');

      }
      this.checkoutForm.markAllAsTouched();
      return;
    }

    const cardInput = this.cardInput();

    if(!cardInput){
      this.toast.error('Payment gateway reference could not be found.');
      return;
    }

    this.loading.set(true);
    cardInput.submitIntent();
  }

  // unmountCard(): void {
  //   this.cardSelector()?.unmountCard();
  // }

  // createIntent(rebuild: boolean = false): void {
  //   console.log('createIntent');

  //   const elements = this.elements();
  //   const card = this.card();
  //   let plan = this.plan();

  //   if(plan == 'gift'){
  //     plan = 'yearly';
  //   }
    

  //   if(card){//? if setup already exists, stop here unless we want to 'rebuild' setup
  //     if(rebuild){
  //       this.unmountCard();
  //     }
  //     else 
  //       return;
  //   }

  //   this.profile.createCouponPayment(plan).pipe(take(1)).subscribe(result => {

  //     if(!result || !result.payment_intent){
  //       console.warn('no intent');
  //       return;
  //     }

  //     this.gift_mode.set(!!result.is_gift);
  //     const toDisable = [];
  //     if(this.gift_mode()){
  //       toDisable.push('start', 'end');
  //     } else {
  //       toDisable.push('recipient', 'recipient_email', 'message');
  //     }

  //     toDisable.forEach(field => this.checkoutForm.get(field)?.disable());

  //     this.pkg.set(result.package);

  //     // const minDateTime = DateTime.fromJSDate(this.minDate);
  //     const maxDateTime = DateTime.fromJSDate(this.maxDate);

  //     const pkg = this.pkg();
  //     if(pkg?.prompt_end){
  //       this.checkoutForm.get('start')!.valueChanges.subscribe((value: DateTime) => {
  //         const previousStart = this.endMinDateTime();
  //         this.endMinDateTime.set(value);
  //         this.endMaxDateTime.set(DateTime.min(maxDateTime, value.plus({ [pkg.unit??'year']: pkg.duration??1 })));

  //         const end = this.checkoutForm.get('end') as FormControl<DateTime|null>;

  //         if(end!.value){
  //           const diff = end?.value.diff(previousStart, 'day').as('days');
  //           const newEnd = DateTime.min(value.plus({ days: diff }), maxDateTime);
  //           end.setValue(newEnd);
  //         }
  //       });
  //     } else {
  //       this.checkoutForm.get('end')!.disable();
  //     }

  //     if(!pkg?.prompt_start){
  //       this.checkoutForm.get('start')!.disable();
  //     }

  //     if(result.email){
  //       this.checkoutForm.get('email')!.setValue(result.email);
  //     }

  //     if(result.fullname){
  //       this.checkoutForm.get('fullname')!.setValue(result.fullname);
  //     }

  //     this.checkoutForm.get('start')!.setValue(DateTime.now());
  //     this.checkoutForm.get('end')!.setValue(DateTime.min(DateTime.now().plus({ [pkg?.unit??'year']: pkg?.duration??1 }), maxDateTime));

  //     const cardSelector = this.cardSelector();

  //     if(cardSelector){
  //       cardSelector.renderClient(result.payment_intent);
  //     }
  //   });
  // }

}
